import { useCallback, useState, useRef, useEffect } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { mergeAttributes, Node } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import Youtube from '@tiptap/extension-youtube';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Quote, Code, Link as LinkIcon,
  Image as ImageIcon, Table as TableIcon,
  Minus, Undo, Redo, Trash2,
  AlignLeft as AlignLeftImg, AlignCenter as AlignCenterImg,
  AlignRight as AlignRightImg, Maximize,
  Video as VideoIcon, Film as FilmIcon, Loader2,
  X, Check, CheckSquare, PlusSquare, MinusSquare, Clock, AlertCircle, Eye, Copy,
} from 'lucide-react';
import { apis } from '../../../core/services/params';
import axiosInstance from '../../../core/services/axiosConfig';

// ─────────────────────────────────────────────────────────────────────────────
// Modal primitives
// ─────────────────────────────────────────────────────────────────────────────

const Modal = ({ title, onClose, children, width = 420 }: any) => {
  useEffect(() => {
    const onKey = (e: any) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onMouseDown={(e: any) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--surface)', borderRadius: '12px', padding: '24px',
        width, maxWidth: 'calc(100vw - 32px)',
        boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
        border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <span style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', borderRadius: '4px', display: 'flex' }}>
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const ModalInput = ({ label, value, onChange, placeholder, type = 'text', autoFocus }: any) => (
  <div style={{ marginBottom: '14px' }}>
    {label && <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>}
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      style={{
        width: '100%', padding: '9px 12px', borderRadius: '7px',
        border: '1px solid var(--border)', background: 'var(--surface-dark)',
        color: 'var(--text-primary)', fontSize: '14px', outline: 'none',
        boxSizing: 'border-box', transition: 'border-color 0.2s',
      } as any}
      onFocus={(e: any) => e.target.style.borderColor = 'var(--primary)'}
      onBlur={(e: any) => e.target.style.borderColor = 'var(--border)'}
    />
  </div>
);

const ModalActions = ({ onCancel, onConfirm, confirmLabel = 'Insert', confirmDisabled }: any) => (
  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
    <button onClick={onCancel} style={{
      padding: '8px 16px', borderRadius: '7px', border: '1px solid var(--border)',
      background: 'transparent', color: 'var(--text-muted)', fontSize: '13px',
      cursor: 'pointer', fontWeight: 500,
    }}>Cancel</button>
    <button onClick={onConfirm} disabled={confirmDisabled} style={{
      padding: '8px 18px', borderRadius: '7px', border: 'none',
      background: confirmDisabled ? 'var(--border)' : 'var(--primary)',
      color: '#fff', fontSize: '13px', cursor: confirmDisabled ? 'not-allowed' : 'pointer',
      fontWeight: 600, transition: 'opacity 0.2s',
    }}>{confirmLabel}</button>
  </div>
);

const PreviewModal = ({ html, friendlyUrl, onClose }: any) => {
  const [remoteHtml, setRemoteHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!friendlyUrl) return;
    setLoading(true);
    axiosInstance.get(`${apis.GET_LAYOUT_JSON_BY_REQUEST_PARAM}${friendlyUrl}`)
      .then((res) => {
        const data = res.data;
        if (data?.data?.data) {
          const components = data.data.data;
          const textComp = components.find((c: any) => c.componentLabel === 'text_component');
          if (textComp && textComp.data && textComp.data.length > 0) {
            setRemoteHtml(textComp.data[0].textDescription);
          } else {
             setRemoteHtml('<p>No text component found in API response.</p>');
          }
        } else {
          setRemoteHtml('<p>Failed to load data from API.</p>');
        }
      })
      .catch((err) => {
        console.error(err);
        setRemoteHtml('<p>Error loading data.</p>');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [friendlyUrl]);

  return (
    <Modal title="HTML Preview" onClose={onClose} width={1200}>
      <div style={{ display: 'flex', gap: '24px', maxHeight: '70vh' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--surface)' }}>
          <h3 style={{ marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>Current Draft</h3>
          <div className="ProseMirror" dangerouslySetInnerHTML={{ __html: html }} style={{ padding: 0, minHeight: 'auto', outline: 'none', border: 'none', boxShadow: 'none' }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--surface)' }}>
          <h3 style={{ marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>Live Preview</h3>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>Loading...</div>
          ) : (
            <div className="ProseMirror" dangerouslySetInnerHTML={{ __html: remoteHtml || '<p>No live data available.</p>' }} style={{ padding: 0, minHeight: 'auto', outline: 'none', border: 'none', boxShadow: 'none' }} />
          )}
        </div>
      </div>
    </Modal>
  );
};

const LinkModal = ({ initialUrl = '', onClose, onSubmit }: any) => {
  const [url, setUrl] = useState(initialUrl);
  const [newTab, setNewTab] = useState(true);
  return (
    <Modal title="Insert Link" onClose={onClose}>
      <ModalInput label="URL" value={url} onChange={setUrl} placeholder="https://example.com" autoFocus />
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-secondary)' }}>
        <input type="checkbox" checked={newTab} onChange={(e) => setNewTab(e.target.checked)} style={{ accentColor: 'var(--primary)' } as any} />
        Open in new tab
      </label>
      <ModalActions onCancel={onClose} confirmLabel="Apply" confirmDisabled={!url.trim()} onConfirm={() => { onSubmit(url.trim(), newTab); onClose(); }} />
    </Modal>
  );
};

const ImageModal = ({ onClose, onSubmit, onUpload, isUploading }: any) => {
  const [tab, setTab] = useState('url');
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const tabStyle = (active: any) => ({
    padding: '6px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: 500,
    background: active ? 'var(--primary)' : 'transparent',
    color: active ? '#fff' : 'var(--text-muted)',
    transition: 'all 0.15s',
  } as any);

  return (
    <Modal title="Insert Image" onClose={onClose}>
      <div style={{ display: 'flex', gap: '4px', background: 'var(--surface-dark)', borderRadius: '8px', padding: '4px', marginBottom: '16px', width: 'fit-content' }}>
        <button style={tabStyle(tab === 'url')} onClick={() => setTab('url')}>URL</button>
        <button style={tabStyle(tab === 'upload')} onClick={() => setTab('upload')}>Upload</button>
      </div>

      {tab === 'url' ? (
        <>
          <ModalInput label="Image URL" value={url} onChange={setUrl} placeholder="https://example.com/image.jpg" autoFocus />
          <ModalInput label="Alt Text (accessibility)" value={alt} onChange={setAlt} placeholder="Describe the image…" />
          <ModalActions onCancel={onClose} confirmDisabled={!url.trim()} onConfirm={() => { onSubmit(url.trim(), alt.trim()); onClose(); }} />
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '20px 0' } as any}>
          <input type="file" accept="image/*" ref={fileRef} style={{ display: 'none' }}
            onChange={async (e) => { const f = e.target.files?.[0]; if (f) { await onUpload(f, alt); onClose(); } }} />
          <ModalInput label="Alt Text (optional)" value={alt} onChange={setAlt} placeholder="Describe the image…" />
          <button disabled={isUploading} onClick={() => fileRef.current?.click()} style={{
            padding: '10px 20px', borderRadius: '8px', border: '2px dashed var(--border)',
            background: 'var(--surface-dark)', color: 'var(--text-secondary)', cursor: 'pointer',
            fontSize: '14px', width: '100%', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}>
            {isUploading ? <><Loader2 size={16} className="spin" /> Uploading…</> : <><ImageIcon size={16} /> Choose image file</>}
          </button>
        </div>
      )}
    </Modal>
  );
};

const YoutubeModal = ({ onClose, onSubmit }: any) => {
  const [url, setUrl] = useState('');
  return (
    <Modal title="Embed YouTube Video" onClose={onClose}>
      <ModalInput label="YouTube URL" value={url} onChange={setUrl} placeholder="https://www.youtube.com/watch?v=..." autoFocus />
      <ModalActions onCancel={onClose} confirmLabel="Embed" confirmDisabled={!url.trim()} onConfirm={() => { onSubmit(url.trim()); onClose(); }} />
    </Modal>
  );
};

const VideoModal = ({ onClose, onSubmit, onUpload, isUploading }: any) => {
  const [tab, setTab] = useState('url');
  const [url, setUrl] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const tabStyle = (active: any) => ({
    padding: '6px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: 500,
    background: active ? 'var(--primary)' : 'transparent',
    color: active ? '#fff' : 'var(--text-muted)',
  } as any);

  return (
    <Modal title="Insert Video" onClose={onClose}>
      <div style={{ display: 'flex', gap: '4px', background: 'var(--surface-dark)', borderRadius: '8px', padding: '4px', marginBottom: '16px', width: 'fit-content' }}>
        <button style={tabStyle(tab === 'url')} onClick={() => setTab('url')}>URL</button>
        <button style={tabStyle(tab === 'upload')} onClick={() => setTab('upload')}>Upload</button>
      </div>
      {tab === 'url' ? (
        <>
          <ModalInput label="Video URL (.mp4, .webm…)" value={url} onChange={setUrl} placeholder="https://example.com/video.mp4" autoFocus />
          <ModalActions onCancel={onClose} confirmLabel="Insert" confirmDisabled={!url.trim()} onConfirm={() => { onSubmit(url.trim()); onClose(); }} />
        </>
      ) : (
        <div>
          <input type="file" accept="video/*" ref={fileRef} style={{ display: 'none' }}
            onChange={async (e) => { const f = e.target.files?.[0]; if (f) { await onUpload(f); onClose(); } }} />
          <button disabled={isUploading} onClick={() => fileRef.current?.click()} style={{
            padding: '10px 20px', borderRadius: '8px', border: '2px dashed var(--border)',
            background: 'var(--surface-dark)', color: 'var(--text-secondary)', cursor: 'pointer',
            fontSize: '14px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}>
            {isUploading ? <><Loader2 size={16} className="spin" /> Uploading…</> : <><FilmIcon size={16} /> Choose video file</>}
          </button>
          <ModalActions onCancel={onClose} confirmLabel="Insert" confirmDisabled onConfirm={() => { }} />
        </div>
      )}
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Media Toolbar (floating above selected media)
// ─────────────────────────────────────────────────────────────────────────────

const MediaToolbar = ({ align, width, updateAttributes, deleteNode }: any) => {
  const btnBase = { padding: '6px', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' } as any;
  const alignBtn = (val: any) => ({ ...btnBase, background: align === val ? 'var(--primary)' : 'transparent', color: align === val ? '#fff' : 'var(--text-secondary)' });
  const sizeBtn = (val: any) => ({ ...btnBase, background: width === val || (!width && val === '100%') ? 'var(--primary)' : 'transparent', color: width === val || (!width && val === '100%') ? '#fff' : 'var(--text-secondary)', fontSize: '11px', fontWeight: 'bold', padding: '4px 8px' });

  return (
    <div contentEditable={false} style={{
      position: 'absolute', top: '-48px',
      ...(align === 'left' ? { left: 0 } : align === 'right' ? { right: 0 } : { left: '50%', transform: 'translateX(-50%)' }),
      zIndex: 100, display: 'flex', gap: '4px', background: 'var(--surface)',
      borderRadius: '8px', padding: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)', border: '1px solid var(--border)', whiteSpace: 'nowrap',
    } as any}>
      <button style={alignBtn('left')} title="Float Left" onMouseDown={(e) => { e.preventDefault(); updateAttributes({ align: 'left' }); }}><AlignLeftImg size={15} /></button>
      <button style={alignBtn('center')} title="Center" onMouseDown={(e) => { e.preventDefault(); updateAttributes({ align: 'center' }); }}><AlignCenterImg size={15} /></button>
      <button style={alignBtn('right')} title="Float Right" onMouseDown={(e) => { e.preventDefault(); updateAttributes({ align: 'right' }); }}><AlignRightImg size={15} /></button>
      <button style={alignBtn('none')} title="Full Width" onMouseDown={(e) => { e.preventDefault(); updateAttributes({ align: 'none' }); }}><Maximize size={15} /></button>
      <span style={{ width: '1px', background: 'var(--border)', margin: '4px' }} />
      {['25%', '50%', '75%', '100%'].map((s) => <button key={s} style={sizeBtn(s)} onMouseDown={(e) => { e.preventDefault(); updateAttributes({ width: s }); }}>{s}</button>)}
      <span style={{ width: '1px', background: 'var(--border)', margin: '4px' }} />
      <button style={{ ...btnBase, color: '#ef4444' }} title="Delete" onMouseDown={(e) => { e.preventDefault(); deleteNode(); }}><Trash2 size={15} /></button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ResizableMediaWrapper
// ─────────────────────────────────────────────────────────────────────────────

const ResizableMediaWrapper = ({ node, updateAttributes, deleteNode, selected, children }: any) => {
  const [showTools, setShowTools] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const align = node.attrs.align || 'none';
  const width = node.attrs.width || '100%';
  const [isResizing, setIsResizing] = useState(false);
  const resizeDir = useRef<any>(null);
  const initialW = useRef(0);
  const initialX = useRef(0);

  // Stable updateAttributes ref so resize effect never goes stale
  const updateAttrsRef = useRef(updateAttributes);
  useEffect(() => { updateAttrsRef.current = updateAttributes; }, [updateAttributes]);

  useEffect(() => {
    const handler = (e: any) => {
      if (isResizing) return;
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowTools(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isResizing]);

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: any) => {
      if (!wrapperRef.current || !resizeDir.current) return;
      const container = wrapperRef.current.closest('.ProseMirror') || wrapperRef.current.parentElement;
      const containerW = container?.getBoundingClientRect().width || 800;
      const delta = e.clientX - initialX.current;
      const change = resizeDir.current === 'right' ? delta : -delta;
      const newPct = Math.max(15, Math.min(100, ((initialW.current + change) / containerW) * 100));
      updateAttrsRef.current({ width: `${Math.round(newPct)}%` });
    };
    const onUp = () => { setIsResizing(false); resizeDir.current = null; document.body.style.cursor = ''; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [isResizing]);

  const startResize = (e: any, dir: any) => {
    e.preventDefault(); e.stopPropagation();
    setIsResizing(true);
    resizeDir.current = dir;
    initialX.current = e.clientX;
    initialW.current = wrapperRef.current?.getBoundingClientRect().width || 0;
    document.body.style.cursor = 'ew-resize';
  };

  const wrapperStyle = {
    display: align === 'center' || align === 'none' ? 'block' : 'inline-block',
    position: 'relative', lineHeight: 0, width, maxWidth: '100%',
    ...(align === 'left' ? { float: 'left', margin: '4px 16px 8px 0' } : {}),
    ...(align === 'right' ? { float: 'right', margin: '4px 0 8px 16px' } : {}),
    ...(align === 'center' ? { display: 'flex', justifyContent: 'center', clear: 'both', margin: '8px auto' } : {}),
    ...(align === 'none' ? { clear: 'both', margin: '8px 0' } : {}),
    transition: isResizing ? 'none' : 'width 0.2s ease',
  } as any;

  const isInteractive = selected || showTools;
  const handleStyle = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: '10px', height: '26px', background: 'var(--primary)', borderRadius: '4px', cursor: 'ew-resize', zIndex: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' } as any;

  return (
    <NodeViewWrapper ref={wrapperRef} as="div" style={wrapperStyle} contentEditable={false}>
      {showTools && <MediaToolbar align={align} width={width} updateAttributes={updateAttributes} deleteNode={deleteNode} />}
      <div style={{ position: 'relative', outline: isInteractive ? '2px solid var(--primary)' : 'none', outlineOffset: '2px', borderRadius: '6px', cursor: 'pointer', width: '100%' } as any}
        onClick={() => setShowTools((v) => !v)}>
        {children(showTools)}
        {isInteractive && (
          <>
            <div onMouseDown={(e) => startResize(e, 'left')} style={{ ...handleStyle, left: '-5px' }} />
            <div onMouseDown={(e) => startResize(e, 'right')} style={{ ...handleStyle, right: '-5px' }} />
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Custom Extensions
// ─────────────────────────────────────────────────────────────────────────────

const ImageNodeView = (props: any) => (
  <ResizableMediaWrapper {...props}>
    {() => <img src={props.node.attrs.src} alt={props.node.attrs.alt || ''} title={props.node.attrs.title || ''} style={{ width: '100%', height: 'auto', borderRadius: '6px', display: 'block' }} />}
  </ResizableMediaWrapper>
);

const ImageAlign = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: { default: 'none', parseHTML: (el) => el.getAttribute('data-align') || 'none', renderHTML: (a) => ({ 'data-align': a.align }) },
      width: { default: '100%', parseHTML: (el) => el.getAttribute('data-width') || '100%', renderHTML: (a) => ({ 'data-width': a.width }) },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const { 'data-align': align, 'data-width': width, ...rest } = HTMLAttributes;
    const w = width || '100%';
    const style = align === 'left' ? `float:left;margin:4px 16px 8px 0;display:inline-block;width:${w};`
      : align === 'right' ? `float:right;margin:4px 0 8px 16px;display:inline-block;width:${w};`
        : align === 'center' ? `display:block;margin:8px auto;width:${w};`
          : `display:block;clear:both;margin:8px 0;width:${w};`;
    return ['span', { style }, ['img', mergeAttributes(rest, { 'data-align': align, 'data-width': w, style: 'width:100%;height:auto;' })]];
  },
  addNodeView() { return ReactNodeViewRenderer(ImageNodeView); },
});

const getEmbedUrl = (url: string) => {
  if (!url || url.includes('/embed/')) return url;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/);
  return match?.[1] ? `https://www.youtube.com/embed/${match[1]}` : url;
};

const YoutubeNodeView = (props: any) => (
  <ResizableMediaWrapper {...props}>
    {(showTools: any) => (
      <div style={{ width: '100%', aspectRatio: '16/9', position: 'relative' }}>
        <iframe src={getEmbedUrl(props.node.attrs.src)} allowFullScreen frameBorder="0" style={{ display: 'block', borderRadius: '6px', width: '100%', height: '100%' }} />
        {!showTools && <div style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'pointer' }} />}
      </div>
    )}
  </ResizableMediaWrapper>
);

const YoutubeAlign = Youtube.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: { default: 'center', parseHTML: (el) => el.getAttribute('data-align') || 'center', renderHTML: (a) => ({ 'data-align': a.align }) },
      width: { default: '100%', parseHTML: (el) => el.getAttribute('data-width') || '100%', renderHTML: (a) => ({ 'data-width': a.width }) },
    };
  },
  addNodeView() { return ReactNodeViewRenderer(YoutubeNodeView); },
});

const VideoNodeView = (props: any) => (
  <ResizableMediaWrapper {...props}>
    {() => <video src={props.node.attrs.src} controls style={{ display: 'block', borderRadius: '6px', width: '100%', maxHeight: '400px' }} />}
  </ResizableMediaWrapper>
);

const VideoAlign = Node.create({
  name: 'video', group: 'block', selectable: true, draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      align: { default: 'center', parseHTML: (el: any) => el.getAttribute('data-align') || 'center', renderHTML: (a) => ({ 'data-align': a.align }) },
      width: { default: '100%', parseHTML: (el: any) => el.getAttribute('data-width') || '100%', renderHTML: (a) => ({ 'data-width': a.width }) },
    };
  },
  parseHTML() { return [{ tag: 'video' }]; },
  renderHTML({ HTMLAttributes }) {
    const { 'data-align': align, 'data-width': width, ...rest } = HTMLAttributes;
    const w = width || '100%';
    const style = align === 'left' ? `float:left;margin:4px 16px 8px 0;display:inline-block;width:${w};`
      : align === 'right' ? `float:right;margin:4px 0 8px 16px;display:inline-block;width:${w};`
        : align === 'center' ? `display:flex;justify-content:center;margin:8px auto;width:${w};`
          : `display:block;clear:both;margin:8px 0;width:${w};`;
    return ['div', { style }, ['video', mergeAttributes(rest, { controls: 'true', 'data-align': align, 'data-width': w, style: 'width:100%;max-width:100%;' })]];
  },
  addNodeView() { return ReactNodeViewRenderer(VideoNodeView); },
});

// ─────────────────────────────────────────────────────────────────────────────
// Table Context Toolbar
// ─────────────────────────────────────────────────────────────────────────────

const TableToolbar = ({ editor }: any) => {
  if (!editor || !editor.isActive('table')) return null;

  const btn = (onClick: any, icon: any, title: any, danger = false) => (
    <button title={title} onMouseDown={(e) => { e.preventDefault(); onClick(); }} style={{
      display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px',
      borderRadius: '5px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
      background: 'transparent', color: danger ? '#ff6b6b' : 'var(--text-secondary)',
      transition: 'background 0.15s',
    } as any}
      onMouseEnter={(e: any) => e.currentTarget.style.background = danger ? 'rgba(255,107,107,0.12)' : 'var(--hover)'}
      onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}
    >
      {icon}{title}
    </button>
  );

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '2px', padding: '6px 10px',
      background: 'var(--surface)', borderTop: '1px solid var(--border)',
      borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', alignSelf: 'center', paddingRight: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Table</span>
      {btn(() => editor.chain().focus().addRowBefore().run(), <PlusSquare size={13} />, 'Row above')}
      {btn(() => editor.chain().focus().addRowAfter().run(), <PlusSquare size={13} />, 'Row below')}
      {btn(() => editor.chain().focus().deleteRow().run(), <MinusSquare size={13} />, 'Del row', true)}
      <span style={{ width: '1px', background: 'var(--border)', margin: '4px 2px' }} />
      {btn(() => editor.chain().focus().addColumnBefore().run(), <PlusSquare size={13} />, 'Col left')}
      {btn(() => editor.chain().focus().addColumnAfter().run(), <PlusSquare size={13} />, 'Col right')}
      {btn(() => editor.chain().focus().deleteColumn().run(), <MinusSquare size={13} />, 'Del col', true)}
      <span style={{ width: '1px', background: 'var(--border)', margin: '4px 2px' }} />
      {btn(() => editor.chain().focus().toggleHeaderRow().run(), null, 'Toggle header')}
      {btn(() => editor.chain().focus().mergeCells().run(), null, 'Merge cells')}
      {btn(() => editor.chain().focus().splitCell().run(), null, 'Split cell')}
      <span style={{ width: '1px', background: 'var(--border)', margin: '4px 2px' }} />
      {btn(() => editor.chain().focus().deleteTable().run(), <Trash2 size={13} />, 'Delete table', true)}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Toolbar Button
// ─────────────────────────────────────────────────────────────────────────────

const ToolbarBtn = ({ onClick, active, title, disabled, children }: any) => (
  <button type="button" title={title} disabled={disabled}
    onMouseDown={(e) => { e.preventDefault(); if (!disabled) onClick(); }}
    style={{
      padding: '6px', border: 'none', borderRadius: '5px', cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: active ? 'var(--primary)' : 'transparent',
      color: active ? '#fff' : disabled ? 'var(--text-muted)' : 'var(--text-secondary)',
      transition: 'background 0.15s, color 0.15s',
      opacity: disabled ? 0.5 : 1,
    } as any}
    onMouseEnter={(e: any) => { if (!active && !disabled) e.currentTarget.style.background = 'var(--hover)'; }}
    onMouseLeave={(e: any) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
  >
    {children}
  </button>
);

const Divider = () => <span style={{ width: '1px', background: 'var(--border)', margin: '4px 2px', alignSelf: 'stretch' }} />;

// ─────────────────────────────────────────────────────────────────────────────
// Autosave hook with debounce
// ─────────────────────────────────────────────────────────────────────────────

const DRAFT_KEY = 'cms_editor_draft';
const DRAFT_META_KEY = 'cms_editor_draft_meta';

const useDraftAutosave = (html: any, enabled = true) => {
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const timerRef = useRef<any>(null);

  const save = useCallback((content: any) => {
    try {
      localStorage.setItem(DRAFT_KEY, content);
      localStorage.setItem(DRAFT_META_KEY, JSON.stringify({ savedAt: new Date().toISOString() }));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!enabled || !html) return;
    setSaveStatus('saving');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(html), 1200);
    return () => clearTimeout(timerRef.current);
  }, [html, enabled, save]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
    localStorage.removeItem(DRAFT_META_KEY);
  }, []);

  const getDraft = useCallback(() => {
    const content = localStorage.getItem(DRAFT_KEY);
    const meta = localStorage.getItem(DRAFT_META_KEY);
    return { content, meta: meta ? JSON.parse(meta) : null };
  }, []);

  return { saveStatus, clearDraft, getDraft };
};

// ─────────────────────────────────────────────────────────────────────────────
// Word & character count
// ─────────────────────────────────────────────────────────────────────────────

const getStats = (editor: any) => {
  if (!editor) return { words: 0, chars: 0, readTime: 0 };
  const text = editor.state.doc.textContent;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const chars = text.length;
  const readTime = Math.max(1, Math.ceil(words / 200));
  return { words, chars, readTime };
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Editor Component
// ─────────────────────────────────────────────────────────────────────────────

const TextEditor = ({ articlePayload, setArticlePayload }: any) => {
  const content = articlePayload?.htmlContent || '';
  const [html, setHtml] = useState(content);
  const [isUploading, setIsUploading] = useState(false);
  const [modal, setModal] = useState<any>(null); // null | 'link' | 'image' | 'youtube' | 'video' | 'preview'
  const [tableActive, setTableActive] = useState(false);
  const [stats, setStats] = useState({ words: 0, chars: 0, readTime: 1 });
  const [copied, setCopied] = useState(false);

  // FIX: Keep a stable ref to articlePayload so useEditor's onUpdate
  // never closes over a stale value.
  const articlePayloadRef = useRef(articlePayload);
  useEffect(() => { articlePayloadRef.current = articlePayload; }, [articlePayload]);

  const closeModal = useCallback(() => setModal(null), []);

  // ── CMS Upload ──
  const uploadToCMS = async (file: File) => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch('https://cmsnode.moglix.com/cmsApi/v1/cms/layout/uploadImage', {
        method: 'POST',
        headers: { 'Referer': 'https://cms-v2.moglix.com/' },
        body: formData,
      });
      if (!res.ok) throw new Error('Network error');
      const result = await res.json();
      if (result.status && result.data?.s3ImageUrl) return result.data.s3ImageUrl;
      throw new Error(result.statusDescription || 'Upload failed');
    } catch (err: any) {
      console.error('Upload error:', err);
      alert(`Upload failed: ${err.message}`);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  // ── Editor ──
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, autolink: true }),
      // allowBase64 disabled — images must go through CMS upload
      ImageAlign.configure({ inline: true, allowBase64: false }),
      YoutubeAlign.configure({ controls: true }),
      VideoAlign,
      Placeholder.configure({ placeholder: 'Start writing your amazing article…' }),
      Table.configure({ resizable: true }),
      TableRow, TableHeader, TableCell,
    ],
    content,
    onUpdate: ({ editor }) => {
      const newHtml = editor.getHTML();
      setHtml(newHtml);
      // FIX: Read from ref — always the latest payload, never stale
      setArticlePayload({ ...articlePayloadRef.current, htmlContent: newHtml });
      setStats(getStats(editor));
      setTableActive(editor.isActive('table'));
    },
    onSelectionUpdate: ({ editor }) => {
      setTableActive(editor.isActive('table'));
    },

    // Paste handler — intercepts clipboard images and uploads them via CMS
    editorProps: {
      handlePaste(view, event) {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find((i) => i.type.startsWith('image/'));
        if (!imageItem) return false;

        event.preventDefault();
        const file = imageItem.getAsFile();
        if (!file) return true;

        uploadToCMS(file).then((url) => {
          if (url) {
            view.dispatch(
              view.state.tr.replaceSelectionWith(
                view.state.schema.nodes.image.create({ src: url, alt: '' })
              )
            );
          }
        });
        return true;
      },
    },
  });

  // Initial stats
  useEffect(() => {
    if (editor) setStats(getStats(editor));
  }, [editor]);

  // Sync content from outside if it changes (e.g. from restore)
  useEffect(() => {
    if (editor && content !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // Autosave
  const { saveStatus, getDraft } = useDraftAutosave(html, true);



  // ── Link handler ──
  const handleLinkSubmit = useCallback((url: any, newTab: any) => {
    if (!editor) return;
    if (!url) {
      if (editor.isActive('link')) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
      }
      return;
    }
    if (editor.isActive('link')) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: newTab ? '_blank' : '_self' }).run();
    } else {
      editor.chain().focus().setLink({ href: url, target: newTab ? '_blank' : '_self' }).run();
    }
  }, [editor]);

  // ── Copy HTML handler ──
  const handleCopyHtml = useCallback(() => {
    navigator.clipboard.writeText(html);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [html]);

  // ── Image handlers ──
  const handleImageSubmit = useCallback((url: any, alt: any) => {
    editor?.chain().focus().setImage({ src: url, alt }).run();
  }, [editor]);

  const handleImageUpload = useCallback(async (file: any, alt: any) => {
    const url = await uploadToCMS(file);
    if (url) editor?.chain().focus().setImage({ src: url, alt: alt || '' }).run();
  }, [editor]);

  // ── YouTube handler ──
  const handleYoutubeSubmit = useCallback((url: any) => {
    (editor?.commands as any).setYoutubeVideo({ src: url });
  }, [editor]);

  // ── Video handlers ──
  const handleVideoSubmit = useCallback((url: any) => {
    editor?.chain().focus().insertContent({ type: 'video', attrs: { src: url } }).run();
  }, [editor]);

  const handleVideoUpload = useCallback(async (file: any) => {
    const url = await uploadToCMS(file);
    if (url) editor?.chain().focus().insertContent({ type: 'video', attrs: { src: url } }).run();
  }, [editor]);

  // ── Table insert ──
  const addTable = useCallback(() => {
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  // ── Alignment helpers ──
  const handleAlign = (alignment: any) => {
    if (!editor) return;
    if (editor.isActive('image')) editor.chain().focus().updateAttributes('image', { align: alignment }).run();
    else if (editor.isActive('youtube')) editor.chain().focus().updateAttributes('youtube', { align: alignment }).run();
    else if (editor.isActive('video')) editor.chain().focus().updateAttributes('video', { align: alignment }).run();
    else editor.chain().focus().setTextAlign(alignment).run();
  };

  const getActiveAlign = (a: any) => {
    if (!editor) return false;
    if (editor.isActive('image')) return editor.getAttributes('image').align === a;
    if (editor.isActive('youtube')) return editor.getAttributes('youtube').align === a;
    if (editor.isActive('video')) return editor.getAttributes('video').align === a;
    return editor.isActive({ textAlign: a });
  };

  if (!editor) return null;

  // ── Save status badge ──
  const statusMap: any = {
    saving: { icon: <Clock size={12} />, label: 'Saving…', color: 'var(--text-muted)' },
    saved: { icon: <Check size={12} />, label: 'Draft saved', color: '#4caf50' },
    error: { icon: <AlertCircle size={12} />, label: 'Save failed', color: '#ff6b6b' },
    idle: null,
  };
  const statusBadge = statusMap[saveStatus];

  // FIX: Read live color values from editor state for the color pickers
  const activeTextColor = (editor.getAttributes('textStyle').color as string) || '#0f172a';
  const activeHighlightColor = (editor.getAttributes('highlight').color as string) || '#FFFF00';

  return (
    <>
      <style>{`
        :root {
          --primary: #2563eb;
          --surface: #ffffff;
          --surface-dark: #f8f9fb;
          --border: #e2e8f0;
          --text-primary: #0f172a;
          --text-secondary: #475569;
          --text-muted: #94a3b8;
          --hover: #f1f5f9;
          --radius: 10px;
        }

        .editor-shell {
          font-family: 'Georgia', serif;
          border-radius: 12px;
          border: 1px solid var(--border);
          background: var(--surface);
          box-shadow: 0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05);
          display: flex;
          flex-direction: column;
        }

        .editor-toolbar {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 2px;
          padding: 8px 10px;
          background: var(--surface-dark);
          border-bottom: 1px solid var(--border);
          position: sticky;
          top: 70px;
          z-index: 100;
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
        }

        .heading-select {
          padding: 5px 8px;
          border-radius: 6px;
          border: 1px solid var(--border);
          background: var(--surface);
          color: var(--text-secondary);
          font-size: 13px;
          cursor: pointer;
          outline: none;
        }

        .color-picker-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 5px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .color-picker-wrapper:hover { background: var(--hover); }
        .color-picker-wrapper input[type=color] {
          position: absolute;
          inset: 0;
          opacity: 0;
          width: 100%;
          height: 100%;
          cursor: pointer;
          border: none;
          padding: 0;
        }
        .color-indicator {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          border: 2px solid var(--border);
          flex-shrink: 0;
        }
        .color-label {
          font-size: 12px;
          font-weight: 700;
          color: var(--text-secondary);
        }

        .ProseMirror {
          padding: 28px 36px;
          min-height: 420px;
          outline: none;
          color: var(--text-primary);
          line-height: 1.8;
          font-size: 16px;
        }

        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: var(--text-muted);
          pointer-events: none;
          height: 0;
        }

        .ProseMirror h1 { font-size: 2em; margin: 0.75em 0 0.3em; font-weight: 700; line-height: 1.2; }
        .ProseMirror h2 { font-size: 1.5em; margin: 0.65em 0 0.25em; font-weight: 700; }
        .ProseMirror h3 { font-size: 1.25em; margin: 0.6em 0 0.2em; font-weight: 600; }
        .ProseMirror h4 { font-size: 1.1em; margin: 0.5em 0 0.15em; font-weight: 600; }

        .ProseMirror blockquote {
          border-left: 3px solid var(--primary);
          margin: 12px 0;
          padding: 8px 16px;
          background: rgba(37,99,235,0.06);
          border-radius: 0 6px 6px 0;
          color: var(--text-secondary);
        }

        .ProseMirror code { font-family: 'Fira Code', monospace; background: rgba(255,255,255,0.08); padding: 2px 6px; border-radius: 4px; font-size: 0.88em; }
        .ProseMirror pre { background: var(--surface-dark); border-radius: 8px; padding: 16px; overflow-x: auto; border: 1px solid var(--border); }
        .ProseMirror pre code { background: none; padding: 0; }

        .ProseMirror ul, .ProseMirror ol { padding-left: 1.5em; margin: 8px 0; }
        .ProseMirror ul { list-style-type: disc; }
        .ProseMirror ol { list-style-type: decimal; }
        .ProseMirror li { margin: 4px 0; }

        .ProseMirror ul[data-type="taskList"] {
          list-style: none;
          padding: 0;
        }
        .ProseMirror ul[data-type="taskList"] li {
          display: flex;
          align-items: flex-start;
          margin: 4px 0;
        }
        .ProseMirror ul[data-type="taskList"] li > label {
          margin-right: 8px;
          margin-top: 2px;
          user-select: none;
          cursor: pointer;
        }
        .ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"] {
          accent-color: #f59e0b;
          width: 16px;
          height: 16px;
          cursor: pointer;
        }
        .ProseMirror ul[data-type="taskList"] li > div {
          flex: 1;
        }

        .ProseMirror a, .ProseMirror a * { color: #2563eb !important; text-decoration: underline !important; cursor: pointer !important; }

        .ProseMirror hr { border: none; border-top: 1px solid var(--border); margin: 24px 0; }

        .ProseMirror table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
          table-layout: fixed;
        }
        .ProseMirror td, .ProseMirror th {
          border: 1px solid var(--border);
          padding: 8px 12px;
          font-size: 14px;
          vertical-align: top;
          min-width: 80px;
          position: relative;
        }
        .ProseMirror th { background: rgba(37,99,235,0.1); font-weight: 600; color: var(--text-primary); }
        .ProseMirror .selectedCell:after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(37,99,235,0.15);
          pointer-events: none;
        }
        .ProseMirror .column-resize-handle {
          position: absolute;
          right: -2px;
          top: 0;
          bottom: 0;
          width: 4px;
          background: var(--primary);
          cursor: col-resize;
        }

        .editor-status-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 14px;
          background: var(--surface-dark);
          border-top: 1px solid var(--border);
          font-size: 12px;
          color: var(--text-muted);
          gap: 16px;
        }


        .upload-overlay {
          position: absolute;
          inset: 0;
          background: rgba(248,249,251,0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 200;
          border-radius: 12px;
          backdrop-filter: blur(4px);
        }
        .upload-status {
          display: flex;
          align-items: center;
          gap: 10px;
          background: var(--surface);
          padding: 14px 24px;
          border-radius: 10px;
          color: var(--text-primary);
          font-size: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08);
          border: 1px solid var(--border);
        }

        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>


      <div className="editor-shell" style={{ position: 'relative' }}>
        {/* ── Toolbar ── */}
        <div className="editor-toolbar">
          <select className="heading-select"
            value={
              editor.isActive('heading', { level: 1 }) ? '1'
                : editor.isActive('heading', { level: 2 }) ? '2'
                  : editor.isActive('heading', { level: 3 }) ? '3'
                    : editor.isActive('heading', { level: 4 }) ? '4'
                      : '0'
            }
            onChange={(e) => {
              const lvl = parseInt(e.target.value);
              if (lvl === 0) editor.chain().focus().setParagraph().run();
              else editor.chain().focus().toggleHeading({ level: lvl as any }).run();
            }}>
            <option value="0">Paragraph</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
            <option value="4">Heading 4</option>
          </select>

          <Divider />

          <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (Ctrl+B)"><Bold size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (Ctrl+I)"><Italic size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline (Ctrl+U)"><UnderlineIcon size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><Strikethrough size={15} /></ToolbarBtn>

          <Divider />

          <ToolbarBtn onClick={() => handleAlign('left')} active={getActiveAlign('left')} title="Align Left"><AlignLeft size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => handleAlign('center')} active={getActiveAlign('center')} title="Align Center"><AlignCenter size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => handleAlign('right')} active={getActiveAlign('right')} title="Align Right"><AlignRight size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => handleAlign('justify')} active={getActiveAlign('justify')} title="Justify"><AlignJustify size={15} /></ToolbarBtn>

          <Divider />

          <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List"><List size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List"><ListOrdered size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive('taskList')} title="Task List"><CheckSquare size={15} /></ToolbarBtn>

          <Divider />

          <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote"><Quote size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code Block"><Code size={15} /></ToolbarBtn>

          <Divider />

          {/* FIX: Use `value` (not `defaultValue`) so the indicator reflects
              the actual color of the currently selected text */}
          <div className="color-picker-wrapper" title="Text Color">
            <div className="color-indicator" style={{ background: activeTextColor }} />
            <span className="color-label">A</span>
            <input
              type="color"
              value={activeTextColor}
              onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            />
          </div>

          <div className="color-picker-wrapper" title="Highlight">
            <div className="color-indicator" style={{ background: activeHighlightColor }} />
            <span className="color-label">H</span>
            <input
              type="color"
              value={activeHighlightColor}
              onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
            />
          </div>

          <Divider />

          <ToolbarBtn onClick={() => setModal('link')} active={editor.isActive('link')} title="Insert Link" disabled={isUploading}><LinkIcon size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => setModal('image')} active={false} title="Insert Image" disabled={isUploading}><ImageIcon size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => setModal('video')} active={false} title="Insert Video" disabled={isUploading}><FilmIcon size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => setModal('youtube')} active={false} title="Embed YouTube" disabled={isUploading}><VideoIcon size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={addTable} active={editor.isActive('table')} title="Insert Table" disabled={isUploading}><TableIcon size={15} /></ToolbarBtn>

          <Divider />

          <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} active={false} title="Horizontal Rule"><Minus size={15} /></ToolbarBtn>

          <Divider />

          <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} active={false} title="Undo (Ctrl+Z)" disabled={!editor.can().undo()}><Undo size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} active={false} title="Redo (Ctrl+Y)" disabled={!editor.can().redo()}><Redo size={15} /></ToolbarBtn>

          <Divider />

          <ToolbarBtn onClick={() => setModal('preview')} active={modal === 'preview'} title="Preview HTML"><Eye size={15} /></ToolbarBtn>
          <ToolbarBtn onClick={handleCopyHtml} active={false} title="Copy HTML">
            {copied ? <Check size={15} color="#4caf50" /> : <Copy size={15} />}
          </ToolbarBtn>
        </div>

        {/* Table Context Toolbar */}
        {tableActive && <TableToolbar editor={editor} />}

        {/* Editor Content */}
        <EditorContent editor={editor} />

        {/* Upload Overlay */}
        {isUploading && (
          <div className="upload-overlay">
            <div className="upload-status">
              <Loader2 size={20} className="spin" />
              Uploading media…
            </div>
          </div>
        )}

        {/* Status Bar */}
        <div className="editor-status-bar">
          <div style={{ display: 'flex', gap: '16px' }}>
            <span>{stats.words.toLocaleString()} words</span>
            <span>{stats.chars.toLocaleString()} chars</span>
            <span>~{stats.readTime} min read</span>
          </div>
          {statusBadge && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: statusBadge.color }}>
              {statusBadge.icon}
              <span>{statusBadge.label}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {modal === 'preview' && (
        <PreviewModal html={html} friendlyUrl={articlePayload?.friendlyUrl} onClose={closeModal} />
      )}
      {modal === 'link' && (
        <LinkModal
          initialUrl={editor.getAttributes('link').href || ''}
          onClose={closeModal}
          onSubmit={handleLinkSubmit}
        />
      )}
      {modal === 'image' && (
        <ImageModal
          onClose={closeModal}
          onSubmit={handleImageSubmit}
          onUpload={handleImageUpload}
          isUploading={isUploading}
        />
      )}
      {modal === 'youtube' && (
        <YoutubeModal onClose={closeModal} onSubmit={handleYoutubeSubmit} />
      )}
      {modal === 'video' && (
        <VideoModal
          onClose={closeModal}
          onSubmit={handleVideoSubmit}
          onUpload={handleVideoUpload}
          isUploading={isUploading}
        />
      )}
    </>
  );
};

export default TextEditor;